'use client';

import { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@shared/ui';
import { cn } from '@shared/utils';

interface LandingPageCardProps {
  /** Optional icon component */
  icon?: LucideIcon;
  /** Card title */
  title: string;
  /** Card description */
  description: string;
  /** Optional icon background color classes */
  iconBg?: string;
  /** Optional icon size classes (default: w-6 h-6) */
  iconSize?: string;
  /** Optional icon container padding classes (default: p-3) */
  iconPadding?: string;
  /** Optional additional className */
  className?: string;
}

/**
 * @group Landing
 * @summary Centralized card component for landing page sections with centered content
 * 
 * Provides consistent styling and layout for cards used across landing page sections.
 * All content is centered by default.
 */
export function LandingPageCard({
  icon: Icon,
  title,
  description,
  iconBg = 'bg-secondary',
  iconSize = 'w-6 h-6',
  iconPadding = 'p-3',
  className,
}: LandingPageCardProps) {
  return (
    <Card className={cn('bg-card p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow', className)}>
      <CardContent className="flex flex-col items-center text-center p-0">
        {Icon && (
          <div className={cn('inline-flex rounded-full mb-4', iconBg, iconPadding)}>
            <Icon className={cn('text-primary', iconSize)} />
          </div>
        )}
        <h3 className="text-lg font-semibold mb-2 text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}
